import { useEffect, useState, useRef } from "react";
import { EditRobotPropsLiveWithItem } from "./EnvironmentView";
import { Grid2, Typography, ButtonGroup, Button, TextField } from "@mui/material";
import AceEditor from "react-ace";
import "ace-builds/webpack-resolver"; // Ensure AceEditor is imported correctly
// Import AceEditor modes and themes if not already imported
import "ace-builds/src-noconflict/mode-yaml";
import "ace-builds/src-noconflict/mode-sh";
import "ace-builds/src-noconflict/theme-monokai";

const exensionsToMode: { [key: string]: string } = {
  'yaml': 'yaml',
  'yml': 'yaml',
  'sh': 'sh',
  'txt': 'text',
  'json': 'json',
  'env': 'sh',
  'py': 'python',
  'js': 'javascript',
  'ts': 'typescript',
  'html': 'html'
}
export function FileView({ item, signaling }: EditRobotPropsLiveWithItem) {
  const [content, setContent] = useState<string>('');
  const [contentType, setContentType] = useState<string>('sh');
  const [originalContent, setOriginalContent] = useState<string>('');
  const [isRunningShell, setIsRunningShell] = useState(false);
  const logsRef = useRef<HTMLTextAreaElement>(null);
  const [logs, setLogs] = useState<string[]>([]);
  useEffect(() => {
    if (!item || !signaling) return;
    signaling.sendReplace({ fileRead: item.id });
    const contentHandler = (c: string) => {
      const fileExtension = item.id.split('.').pop();
      const mode = exensionsToMode[fileExtension || ''] || 'text';
      setContentType(mode);
      setContent(c);
      setOriginalContent(c);
    };
    const binaryContentHandler = (base64: string) => {
      const byteString = atob(base64);
      // Create ArrayBuffer and Uint8Array for the binary data
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }
      // Create Blob with PNG MIME type
      const blob = new Blob([arrayBuffer], { type: 'image/png' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = item.id;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    signaling.on('fileRead', contentHandler);
    signaling.on('fileBinaryRead', binaryContentHandler);
    const shellLogsHandler = (log: string[]) => {
      // console.log('shellLogsHandler', log);
      setLogs((prev) => {
        const newLogs = [...prev, ...log];
        if (newLogs.length > 256) {
          newLogs.splice(0, newLogs.length - 256);
        }
        return newLogs;
      });
      setTimeout(() => {
        if (logsRef.current) {
          logsRef.current.scrollTop = logsRef.current.scrollHeight;
        }
      }, 1000);
    };
    signaling.on('shellLogs', shellLogsHandler);
    return () => {
      signaling.off('fileRead', contentHandler);
      signaling.off('fileBinaryRead', binaryContentHandler);
      signaling.off('shellLogs', shellLogsHandler);
    };
  }, [item, signaling]);
  if (!item) return <Typography variant="h5" >Loading</Typography>;
  return (
    <>
      <Grid2 container direction={'row'} sx={{ width: '100%', padding: '16px' }}>
        <Typography style={{ marginRight: '20px' }} variant="h4">{item.id}</Typography>
        <ButtonGroup>
          <Button
            disabled={content === originalContent}
            size='large' variant='contained' color='success' onClick={() => {
              signaling.sendReplace({
                fileWrite: {
                  fileName: item.id,
                  content,
                }
              });
              setOriginalContent(content);
            }}>Save</Button>
          <Button size='large' variant='outlined' color='success' onClick={() => {
            signaling.sendReplace({
              fileBinaryRead: item.id,
            });
          }}>Download</Button>
          <Button
            disabled={isRunningShell || content !== originalContent || !item.name.endsWith('.sh')}
            size='large' variant='contained' color='error' onClick={() => {
              setLogs([]);
              setIsRunningShell(true);
              signaling.sendReplace({
                startShell: item.id,
              });
            }}>RUN</Button>
          <Button
            disabled={!isRunningShell}
            size='large' variant='contained' color='error' onClick={() => {
              setIsRunningShell(false);
              signaling.sendReplace({
                stopShell: item.id,
              });
            }}>STOP</Button>
        </ButtonGroup>
      </Grid2>
      <AceEditor
        mode={contentType}
        theme="twilight"
        fontSize={23}
        width="100%"
        height="800px"
        value={content}
        onChange={setContent}
        editorProps={{ $blockScrolling: true }}
        setOptions={{
          showLineNumbers: true,
          tabSize: 2,
        }}
      />
      {isRunningShell && (
        <TextField
          // style={{
          //   marginTop: '10px',
          //   marginLeft: '10px',
          //   marginRight: '10px',
          // }}
          multiline
          rows={16}
          fullWidth
          value={logs.join("\n")}
          slotProps={{
            input: { readOnly: true },
          }}
          inputRef={logsRef}
        />
      )}
    </>
  );
}
